import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { channelsService, type CompPlanServer, type CompScope, type CompServerResult } from '@/services/channels.service';
import { getErrorMessage } from '@/services/api';
import { serverTypeColor, serverTypeLabel } from '@/utils/server-type';
import { planCounts, retryScope, serverChoice } from '@/utils/comp-channels';

export interface CompetitionChannelsRequest {
  action: 'create' | 'delete';
  scope: CompScope;
  /** Servers to show (create: the competition's servers; delete: the servers holding the rows). */
  serverIds: number[];
  /** delete: exactly these rows. */
  legIds?: number[];
}

/**
 * Create / delete competition channels (phase 4): the REAL state of each server first (plan, a dry run), no server
 * ticked by default, an unreachable server shown with the reason and not tickable, then one confirmed run on the ticked
 * servers only, the result per server and "retry the failed ones" (a new preview of those servers).
 */
export default function CompetitionChannelsModal({ competitionId, request, onClose, onDone }: {
  competitionId: number;
  request: CompetitionChannelsRequest | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [plan, setPlan] = useState<CompPlanServer[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<CompServerResult[] | null>(null);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const running = useRef(false);

  const load = useCallback(async (serverIds: number[], fresh = false) => {
    if (!request) return;
    setLoading(true);
    try {
      setPlan(await channelsService.plan(competitionId, request.action, { servers: serverIds, scope: request.scope, ...(request.legIds ? { legIds: request.legIds } : {}), ...(fresh ? { fresh: true } : {}) }));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
      setPlan([]);
    } finally {
      setLoading(false);
    }
  }, [competitionId, request, t]);

  useEffect(() => {
    setPlan(null); setTicked(new Set()); setResults(null); setOpen(new Set());
    if (request && request.serverIds.length > 0) load(request.serverIds);
  }, [request, load]);

  if (!request) return null;
  const isCreate = request.action === 'create';
  const tickedServers = (plan ?? []).filter((s) => ticked.has(s.serverId) && serverChoice(s).selectable);
  const count = tickedServers.reduce((n, s) => n + planCounts(s).act, 0);

  const run = async () => {
    if (running.current || tickedServers.length === 0) return;
    running.current = true;
    setBusy(true);
    try {
      let out: CompServerResult[];
      if (isCreate) {
        const servers = tickedServers.map((s) => s.serverId);
        const r = request.scope === 'special' ? await channelsService.provisionSpecial(competitionId, servers) : await channelsService.provision(competitionId, servers);
        out = r.results ?? [];
      } else {
        const legIds = tickedServers.flatMap((s) => s.items.filter((i) => i.act && i.legId).map((i) => i.legId!));
        out = (await channelsService.deleteByIds(competitionId, legIds)).results;
      }
      setResults(out);
      const failed = out.reduce((n, r) => n + r.failed.length, 0);
      const done = out.reduce((n, r) => n + r.done.length, 0);
      if (failed > 0) toast.error(t(isCreate ? 'compChannels.toastCreatePartial' : 'compChannels.toastDeletePartial', { done, failed }));
      else toast.success(t(isCreate ? 'compChannels.toastCreated' : 'compChannels.toastDeleted', { count: done }));
      onDone();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      running.current = false;
      setBusy(false);
    }
  };

  const retry = () => {
    if (!results) return;
    const { servers } = retryScope(results);
    setResults(null);
    setTicked(new Set());
    load(servers.length > 0 ? servers : request.serverIds);
  };

  const toggle = (id: number) => setTicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleOpen = (id: number) => setOpen((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const reason = (r: string) => t(`compChannels.skip.${r}`, { defaultValue: r });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => { if (!running.current) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="comp-channels-title" className="card w-full max-w-2xl animate-fade-in flex flex-col max-h-[calc(100vh-2rem)]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2">
          <div>
            <h2 id="comp-channels-title" className="text-lg font-semibold text-dark-100">
              {t(isCreate ? (request.scope === 'special' ? 'compChannels.titleCreateSpecial' : 'compChannels.titleCreate') : (request.scope === 'special' ? 'compChannels.titleDeleteSpecial' : 'compChannels.titleDelete'))}
            </h2>
            <p className="text-sm text-dark-400 mt-1">{t(isCreate ? 'compChannels.introCreate' : 'compChannels.introDelete')}</p>
          </div>
          <button type="button" className="ml-auto p-1.5 rounded-lg hover:bg-dark-800 text-dark-400" onClick={() => { if (!running.current) onClose(); }} aria-label={t('common.close')}><X size={16} /></button>
        </div>

        <div className="mt-4 space-y-2 overflow-y-auto">
          {loading && <div className="flex items-center gap-2 text-sm text-dark-400"><Loader2 size={14} className="animate-spin" /> {t('compChannels.reading')}</div>}
          {!loading && plan && plan.length === 0 && <p className="text-sm text-dark-500">{t('compChannels.noServers')}</p>}
          {!loading && !results && plan?.map((s) => {
            const c = planCounts(s);
            const choice = serverChoice(s);
            return (
              <div key={s.serverId} className="rounded-lg border border-dark-700 px-3 py-2">
                <label className={`flex items-center gap-2 text-sm ${choice.selectable ? 'cursor-pointer' : 'opacity-70'}`}>
                  <input type="checkbox" disabled={!choice.selectable || busy} checked={ticked.has(s.serverId) && choice.selectable} onChange={() => toggle(s.serverId)} />
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${serverTypeColor(s.type)}`}>{serverTypeLabel(s.type)}</span>
                  <span className="font-medium text-dark-100">{s.serverName}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.reachable ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                    {s.removed ? t('compChannels.removed') : s.reachable ? t('compChannels.reachable') : t('compChannels.unreachable', { error: s.error || '?' })}
                  </span>
                  {!choice.selectable && <span className="text-xs text-dark-500">{t(choice.reasonKey)}</span>}
                </label>
                <p className="text-xs text-dark-400 mt-1">
                  {t(isCreate ? 'compChannels.countCreate' : 'compChannels.countDelete', { count: c.act })}
                  {c.alreadyDone > 0 && ` · ${t(isCreate ? 'compChannels.countExisting' : 'compChannels.countGone', { count: c.alreadyDone })}`}
                  {c.existsOnServer > 0 && ` · ${t('compChannels.countOnServer', { count: c.existsOnServer })}`}
                  {c.conflict > 0 && ` · ${t('compChannels.countConflict', { count: c.conflict })}`}
                  {c.unknown > 0 && ` · ${t('compChannels.countUnknown', { count: c.unknown })}`}
                  {c.removed > 0 && ` · ${t('compChannels.countRemoved', { count: c.removed })}`}
                  {isCreate && !s.selected && ` · ${t('compChannels.notSelected')}`}
                  {s.items.length > 0 && (
                    <button type="button" className="ml-2 underline text-dark-300" onClick={() => toggleOpen(s.serverId)}>{open.has(s.serverId) ? t('compChannels.hideDetails') : t('compChannels.showDetails')}</button>
                  )}
                </p>
                {open.has(s.serverId) && (
                  <ul className="mt-1 space-y-0.5 max-h-48 overflow-y-auto">
                    {s.items.map((i) => (
                      <li key={`${i.name}-${i.legId ?? ''}`} className={`text-xs break-words ${i.act && !isCreate ? 'text-error-600' : i.act ? 'text-dark-200' : 'text-dark-500'}`}>
                        <span className="font-mono">{i.name}</span>{' '}
                        <span>({t(`compChannels.real.${i.realStatus}`, { defaultValue: i.realStatus })})</span>{' '}
                        {i.act ? <span className="font-medium">{t(`compChannels.op.${i.op}`)}</span> : <span>{reason(i.skipped || 'not-applicable')}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}

          {results && results.map((r) => (
            <div key={r.serverId} className="rounded-lg border border-dark-700 px-3 py-2">
              <p className="text-sm font-medium text-dark-100">{r.serverName}{!r.reachable && <span className="ml-2 text-xs text-error-600">{t('compChannels.unreachable', { error: r.error || '?' })}</span>}</p>
              <p className="text-xs text-dark-400">{t(isCreate ? 'compChannels.resultCreated' : 'compChannels.resultDeleted', { count: r.done.length })}{r.skipped.length > 0 && ` · ${t('compChannels.resultSkipped', { count: r.skipped.length })}`}</p>
              {r.failed.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {r.failed.map((f) => <li key={f.name} className="text-xs text-error-600 break-words"><span className="font-mono">{f.name}</span>: {f.error}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap justify-end gap-2 mt-4">
          {!results && (
            <button type="button" className="btn-secondary text-sm" disabled={loading || busy} title={t('compChannels.refreshHint')} onClick={() => load(request.serverIds, true)}>
              <RefreshCw size={14} /> {t('compChannels.refresh')}
            </button>
          )}
          {results && retryScope(results).servers.length > 0 && (
            <button type="button" className="btn-secondary text-sm" onClick={retry}>{t('compChannels.retryFailed')}</button>
          )}
          <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={onClose}>{results ? t('common.close') : t('common.cancel')}</button>
          {!results && (
            <button type="button" disabled={busy || loading || count === 0} onClick={run} className={`${isCreate ? 'btn-primary' : 'btn-danger'} text-sm`}>
              {busy && <Loader2 size={14} className="animate-spin" />}
              {t(isCreate ? 'compChannels.confirmCreate' : 'compChannels.confirmDelete', { count, servers: tickedServers.map((s) => s.serverName).join(', ') || '-' })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
